import { Button, Col, Form, Input, Row, Select, Spin } from 'antd';
import '../../styles/_orgs.scss';
import { useCreateTeamByLocationIdMutation } from '../../features/organization/organizationService';
import useOrderToast from '../../hooks/useOrderToast';
import {
    HospiceTeamResponse,
    UpsertTeamRequest,
} from '../../models/OrganizationModels';

type FieldType = {
    teamName?: string;
    rxLogicPracticeID?: string;
    scriptSurePracticeID?: string;
    teamCode?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    phoneNumber?: string;
    emailAddress?: string;
    defaultPcpTeamUserId?: string;
};

type Props = {
    selectedLocationId: string;
    selectedTeam?: HospiceTeamResponse | null;
    setTeamModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedTeam: React.Dispatch<
        React.SetStateAction<HospiceTeamResponse | null>
    >;
};

const ExtraFieldLabel = ({ title }: { title: string }) => {
    return <span className="ms-3 text-semi-black-xs">{title}</span>;
};
const ValidationMessage = ({ message }: { message: string }) => {
    return <span className="ms-3">{message}</span>;
};

const OrganizationsNewTeamForm: React.FC<Props> = ({
    selectedLocationId,
    selectedTeam,
    setTeamModalOpen,
    setSelectedTeam,
}) => {
    const [form]: any = Form.useForm();
    const [createTeamByLocationId, { isLoading }] =
        useCreateTeamByLocationIdMutation();
    const { toast } = useOrderToast();
    const initialCountry = 'United States of America';
    const isUpdate = !!selectedTeam;

    const physicianTeamUsers = selectedTeam?.teamUsers?.filter(user => user.isPrescriber);

    const teamUserOptions =
        physicianTeamUsers?.map((user) => ({
            label: user.fullName,
            value: user.userTeamId,
        })) ?? [];
    const defaultPcpTeamUserId = physicianTeamUsers?.find(
        (user) => user.isDefaultPcp,
    )?.userTeamId;

    const handleSubmit = async () => {
        const values = await form.validateFields();
        try {
            const request: UpsertTeamRequest = {
                id: selectedTeam?.id,
                locationId: selectedLocationId,
                teamName: values.teamName,
                rxLogicPracticeID: values.rxLogicPracticeID,
                scriptSurePracticeID: values.scriptSurePracticeID,
                teamCode: values.teamCode,
                addressLine1: values.addressLine1,
                addressLine2: values.addressLine2,
                city: values.city,
                state: values.state,
                zipCode: values.zipCode,
                country: initialCountry,
                phoneNumber: values.phoneNumber,
                emailAddress: values.emailAddress,
                defaultPcpTeamUserId: values.defaultPcpTeamUserId,
            };
            const response: any = await createTeamByLocationId(request);

            if (response && response.data) {
                toast(
                    isUpdate
                        ? 'The team is successfully updated.'
                        : 'The team is successfully added to the location.',
                    'success',
                    2,
                );
                setTeamModalOpen(false);
                setSelectedTeam(null);
                form.resetFields();
            } else {
                toast(
                    `The team ${isUpdate ? 'update' : 'creation'} failed. Please try again.`,
                    'error',
                    2,
                );
            }
        } catch (error) {
            toast(
                `The team ${isUpdate ? 'update' : 'creation'} failed. Please try again.`,
                'error',
                2,
            );
        }
    };

    return (
        <Spin spinning={isLoading}>
            <Form
                form={form}
                name="basic"
                layout="horizontal"
                autoComplete="off"
                className="orgs-new-team-form"
                initialValues={{ country: initialCountry }}
            >
                <Row>
                    <Col span={24}>
                        <Form.Item<FieldType>
                            label={'Team Name'}
                            name="teamName"
                            initialValue={selectedTeam?.teamName}
                            labelCol={{ span: 4 }}
                            wrapperCol={{ span: 11 }}
                            rules={[
                                {
                                    required: true,
                                    message: (
                                        <ValidationMessage message="Please enter Team Name" />
                                    ),
                                },
                            ]}
                            extra={
                                <ExtraFieldLabel title="Practice name should align with ScriptSure and RxLogic" />
                            }
                        >
                            <Input
                                placeholder="Enter Practice Name"
                                className="ms-3 bg-light-blue-thin rounded-1"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={12}>
                        <Form.Item
                            label="RxLogic's Practice ID"
                            name="rxLogicPracticeID"
                            initialValue={selectedTeam?.rxLogicId}
                            labelCol={{ span: 8 }}
                            wrapperCol={{ span: 14 }}
                            rules={[
                                {
                                    required: true,
                                    message: (
                                        <ValidationMessage message="Please enter RxLogic's Practice ID" />
                                    ),
                                },
                            ]}
                            extra={
                                <ExtraFieldLabel title="RxLogic's Practice ID should align with RxLogic" />
                            }
                        >
                            <Input
                                placeholder="Enter RxLogic's Practice ID"
                                className="ms-3 bg-light-blue-thin rounded-1"
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            label="ScriptSure's Practice ID"
                            name="scriptSurePracticeID"
                            initialValue={selectedTeam?.scriptSureId}
                            labelCol={{ span: 8 }}
                            wrapperCol={{ span: 16 }}
                            rules={[
                                {
                                    required: true,
                                    message: (
                                        <ValidationMessage message="Please enter ScriptSure's Practice ID" />
                                    ),
                                },
                            ]}
                            extra={
                                <ExtraFieldLabel title="ScriptSure's Practice ID should align with ScriptSure" />
                            }
                        >
                            <Input
                                placeholder="Enter ScriptSure's Practice ID"
                                className="ms-3 bg-light-blue-thin rounded-1"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <Form.Item<FieldType>
                            label={'Team Code'}
                            name="teamCode"
                            initialValue={selectedTeam?.code}
                            labelCol={{ span: 4 }}
                            wrapperCol={{ span: 7 }}
                            rules={[
                                {
                                    required: true,
                                    message: (
                                        <ValidationMessage message="Please enter Team Code" />
                                    ),
                                },
                            ]}
                            extra={
                                <ExtraFieldLabel title="Practice code should align with RxLogic" />
                            }
                        >
                            <Input
                                className="ms-3 bg-light-blue-thin rounded-1"
                                placeholder="Enter Practice Code"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <Form.Item<FieldType>
                            label={'Address Line 1'}
                            name="addressLine1"
                            initialValue={selectedTeam?.address?.streetAddress}
                            labelCol={{ span: 4 }}
                            wrapperCol={{ span: 20 }}
                            rules={[
                                {
                                    required: true,
                                    message: (
                                        <ValidationMessage message="Please enter Address Line 1" />
                                    ),
                                },
                            ]}
                            extra={
                                <ExtraFieldLabel title="Street Address, P.O. Box, Company Name, C/O" />
                            }
                        >
                            <Input
                                className="new-team-modal-input ms-3 bg-light-blue-thin rounded-1"
                                placeholder="Enter Address Line 1"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={24}>
                        <Form.Item<FieldType>
                            label={'Address Line 2'}
                            name="addressLine2"
                            initialValue={selectedTeam?.address?.streetAddress2}
                            labelCol={{ span: 4 }}
                            wrapperCol={{ span: 20 }}
                            extra={
                                <ExtraFieldLabel title="Apartment, Suite, Unit, Building, Floor, Etc." />
                            }
                        >
                            <Input
                                className="new-team-modal-input ms-3 bg-light-blue-thin rounded-1"
                                placeholder="Enter Address Line 2"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={12}>
                        <Form.Item
                            label="City"
                            name="city"
                            initialValue={selectedTeam?.address?.city}
                            labelCol={{ span: 8 }}
                            wrapperCol={{ span: 14 }}
                            rules={[
                                {
                                    required: true,
                                    message: (
                                        <ValidationMessage message="Please enter City" />
                                    ),
                                },
                            ]}
                        >
                            <Input
                                placeholder="Enter City"
                                className="ms-3 bg-light-blue-thin rounded-1"
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            label="State/Province/Region"
                            name="state"
                            initialValue={selectedTeam?.address?.state}
                            labelCol={{ span: 8 }}
                            wrapperCol={{ span: 16 }}
                            rules={[
                                {
                                    required: true,
                                    message: (
                                        <ValidationMessage message="Please enter State/Province/Region" />
                                    ),
                                },
                            ]}
                        >
                            <Input
                                placeholder="Enter State/Province/Region"
                                className="ms-3 bg-light-blue-thin rounded-1"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={12}>
                        <Form.Item
                            label="ZIP/Postal Code"
                            labelCol={{ span: 8 }}
                            wrapperCol={{ span: 14 }}
                            name="zipCode"
                            initialValue={selectedTeam?.address?.zipCode}
                            rules={[
                                {
                                    required: true,
                                    message: (
                                        <ValidationMessage message="Please enter ZIP/Postal Code" />
                                    ),
                                },
                            ]}
                        >
                            <Input
                                placeholder="Enter ZIP/Postal Code"
                                className="ms-3 bg-light-blue-thin rounded-1"
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            label="Country"
                            name="country"
                            labelCol={{ span: 8 }}
                            wrapperCol={{ span: 16 }}
                        >
                            <Input
                                className="ms-3 bg-light-blue-thin rounded-1 input-country"
                                disabled
                                onChange={() => {
                                    form.setFieldsValue({
                                        country: initialCountry,
                                    });
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={12}>
                        <Form.Item<FieldType>
                            label={'Phone Number'}
                            name="phoneNumber"
                            initialValue={selectedTeam?.phone}
                            labelCol={{ span: 8 }}
                            wrapperCol={{ span: 14 }}
                        >
                            <Input
                                className="ms-3 bg-light-blue-thin rounded-1"
                                maxLength={10}
                                placeholder="(_ _ _) (_ _ _) (_ _ _ _)"
                            />
                        </Form.Item>
                    </Col>

                    {isUpdate && (
                        <Col span={12}>
                            <Form.Item<FieldType>
                                label={'Default PCP'}
                                name="defaultPcpTeamUserId"
                                initialValue={defaultPcpTeamUserId}
                                labelCol={{ span: 8 }}
                                wrapperCol={{ span: 16 }}
                            >
                                <Select
                                    allowClear
                                    placeholder="Select Default PCP"
                                    className="ms-3"
                                    options={teamUserOptions}
                                />
                            </Form.Item>
                        </Col>
                    )}
                </Row>
                <Row>
                    <Col span={24}>
                        <Form.Item<FieldType>
                            label={'Email'}
                            name="emailAddress"
                            initialValue={selectedTeam?.email}
                            labelCol={{ span: 4 }}
                            wrapperCol={{ span: 7 }}
                        >
                            <Input
                                placeholder="Enter Email Address"
                                className="ms-3 bg-light-blue-thin rounded-1"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col span={4} offset={4}>
                        <Button
                            loading={isLoading}
                            disabled={isLoading}
                            type="primary"
                            className="w-75 ms-3 bg-light-blue border-0"
                            shape="round"
                            onClick={handleSubmit}
                        >
                            Save
                        </Button>
                    </Col>
                </Row>
            </Form>
        </Spin>
    );
};

export default OrganizationsNewTeamForm;
